// Detalle del equipo SOBRE el cajón del cliente, de SOLO LECTURA: bloquear, liberar y el PIN se
// quedan en Control MDM, adonde lleva el enlace del pie.
//
// Vive en `customers/` y no en `mdm/` porque solo lo abre la ficha 360 y comparte con la tarjeta de
// atrás sus rótulos (modelo, motivo); del dominio MDM consume las piezas, no al revés.
import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { Modal } from '@/kit';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import { useAuth } from '@/hooks/useAuth';
import { useMdmDeviceOperations } from '@/domains/credit/mdm/api/useMdmFleet';
import { MdmAuditTab } from '@/domains/credit/mdm/components/MdmAuditTab';
import { MdmStatusPill } from '@/domains/credit/mdm/components/MdmStatusPill';
import {
    explainMissingHeartbeat,
    NO_AUDIT_PERMISSION,
    NO_VALUE,
    providerContextLabel,
    readHeartbeat,
    readLockPhase,
} from '@/domains/credit/mdm/logic';
import { resolveMdmAbilities } from '@/domains/credit/mdm/permissions';
import type { MdmFleetDevice } from '@/domains/credit/mdm/types';
import { deviceModelLabel, lockedSinceLabel, lockReasonLabel } from '../lib/customerDevices';
import { Field, FILE_MODAL_CLASS, FileModalFrame, KV_GRID } from './CustomerFileUi';

const SECTION_HEAD = 'text-[9.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground';

export interface CustomerDeviceModalProps {
    /** `null` mantiene el modal cerrado; el equipo ya viene entero de la flota. */
    device: MdmFleetDevice | null;
    onClose: () => void;
}

export function CustomerDeviceModal({ device, onClose }: CustomerDeviceModalProps) {
    return (
        <Modal
            isOpen={device !== null}
            onClose={onClose}
            title="Detalle del equipo"
            titleAction={
                device === null ? null : (
                    <MdmStatusPill
                        status={device.mdmStatus}
                        mdmConfirmed={device.mdmConfirmed}
                        lockPhase={readLockPhase(device, new Date())}
                        lockScheduledFor={device.currentLockScheduledFor}
                    />
                )
            }
            description={
                device === null
                    ? undefined
                    : `Crédito ${device.creditRef} · ${providerContextLabel(device)}`
            }
            size="xl"
            className={FILE_MODAL_CLASS}
        >
            {device === null ? null : (
                <FileModalFrame>
                    <DeviceReadOnlyDetail device={device} />
                </FileModalFrame>
            )}
        </Modal>
    );
}

function DeviceReadOnlyDetail({ device }: { device: MdmFleetDevice }) {
    const { permissions } = useAuth();
    const abilities = useMemo(() => resolveMdmAbilities(permissions), [permissions]);
    // Sin el permiso la bitácora ni se pide: el 403 no aporta nada que el gate no sepa ya.
    const auditLog = useMdmDeviceOperations(
        abilities.canViewOperationLog ? device.creditId : undefined,
    );
    // Instante de referencia del heartbeat, fijo mientras el modal está abierto.
    const now = useMemo<Date>(() => new Date(), []);
    const heartbeat = readHeartbeat(device.lastSeenAt, now);

    return (
        <div className="min-h-0 flex-1 overflow-y-auto">
            <dl className={KV_GRID}>
                <Field label="Modelo">{deviceModelLabel(device)}</Field>
                <Field label="IMEI">
                    <span className="font-mono text-[11px]">{device.imei}</span>
                </Field>
                <Field label="Motivo">{lockReasonLabel(device)}</Field>
                <Field label="Bloqueado desde">{lockedSinceLabel(device, now)}</Field>
                <Field label="Último heartbeat" wrap>
                    <span className="tabular-nums">
                        {device.lastSeenAt ? formatDateTimeMx(device.lastSeenAt) : NO_VALUE}
                    </span>
                    <div className="text-[10.5px] leading-snug text-muted-foreground">
                        {device.lastSeenAt
                            ? heartbeat.longLabel
                            : explainMissingHeartbeat(device.mdmStatus)}
                    </div>
                </Field>
            </dl>

            <div className="px-3 pb-3">
                <h4 className={SECTION_HEAD}>Bitácora</h4>
                <div className="mt-2">
                    {abilities.canViewOperationLog ? (
                        <MdmAuditTab
                            device={device}
                            entries={auditLog.entries}
                            isLoading={auditLog.isLoading}
                            error={auditLog.error}
                        />
                    ) : (
                        <p className="text-[11.5px] italic text-muted-foreground">
                            {NO_AUDIT_PERMISSION}
                        </p>
                    )}
                </div>

                {/* La única salida a la otra pantalla: quien vaya a OPERAR el equipo sale por acá. */}
                <div className="mt-3 text-right">
                    <Link
                        to="/credit/mdm"
                        search={{ imei: device.imei }}
                        className="inline-flex items-center gap-1 rounded text-[10.5px] font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                        Abrir en Control MDM <span aria-hidden>→</span>
                    </Link>
                </div>
            </div>
        </div>
    );
}
